import { formatDate } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { Router } from '@angular/router';
import { ChartReadyEvent, ChartType, Column, Row } from 'angular-google-charts';
import { Subscription } from 'rxjs';
import { Client } from '../../../../projects/kiene-core/src/lib/components/client/classes/client';
import { API_BASE_URL_SERVICE } from '../../../../projects/kiene-core/src/lib/kiene-core.config';
import { LocalStorageService } from '../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { MessageService } from '../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { ChangeCurrentClientDialogComponent } from '../../administration/client/change-current-client/change-current-client-dialog/change-current-client-dialog.component';
import { ChangeCurrentClientDialogInput } from '../../administration/client/change-current-client/change-current-client-dialog/change-current-client-dialog';
import { KieneTableService } from '../../components/kiene-table/kiene-table.service';
import { ZeitauswahlChange } from '../zeitauswahl/zeitauswahl-change';
import {
    DashboardKpiWidget,
    DashboardNewUserRow,
    DashboardOverview,
    DashboardSelectedTotal,
    DashboardSeries,
    DashboardSeriesId,
} from './dashboard';

@Component({
    selector: 'kiene-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, OnDestroy {
    zeiteintrag: ZeitauswahlChange;
    overview: DashboardOverview | null = null;
    kpiWidgets: DashboardKpiWidget[] = [];
    seriesMap: Partial<Record<DashboardSeriesId, DashboardSeries>> = {};

    /** Physical farm filter; null = all Betriebe. */
    selectedBetriebId: number | null = null;
    selectedBetriebLabel = '';

    metricOptions: { id: DashboardSeriesId; label: string }[] = [
        { id: 'hit', label: 'HIT gemeldet' },
        { id: 'einstall', label: 'Einstall' },
        { id: 'abgang', label: 'Abgang' },
        { id: 'tot', label: 'Tot' },
    ];
    /** Multi-select filter: which series are drawn on the chart. */
    selectedSeriesIds: DashboardSeriesId[] = ['hit', 'einstall'];
    seriesFilterCtrl = new UntypedFormControl(['hit', 'einstall']);

    selectedTotals: DashboardSelectedTotal[] = [];
    /** Classic LineChart — Material `Line` does not support crosshair. */
    chartType = ChartType.LineChart;
    chartColumns: Column[] = [
        { type: 'date', label: 'Zeitraum' },
        { type: 'number', label: 'HIT gemeldet' },
    ];
    chartData: Row[] = [[new Date(), 0]];
    chartOptions: Record<string, unknown> = {};

    /** Nutzer chart: Aktive Nutzer + Sessions (separate lines). */
    userMetricOptions: { id: DashboardSeriesId; label: string }[] = [
        { id: 'dau', label: 'Aktive Nutzer' },
        { id: 'sessions', label: 'Sessions' },
        { id: 'hit_melder', label: 'HIT-Melder' },
        { id: 'new_users', label: 'Neue Nutzer' },
        { id: 'new_betriebe', label: 'Neue Betriebe' },
    ];
    selectedUserSeriesIds: DashboardSeriesId[] = ['dau', 'sessions', 'hit_melder', 'new_users', 'new_betriebe'];
    userSeriesFilterCtrl = new UntypedFormControl(['dau', 'sessions', 'hit_melder', 'new_users', 'new_betriebe']);
    userSeriesList: DashboardSeries[] = [];
    userTotals: DashboardSelectedTotal[] = [];
    dauChartColumns: Column[] = [
        { type: 'date', label: 'Zeitraum' },
        { type: 'number', label: 'Aktive Nutzer' },
        { type: 'number', label: 'Sessions' },
    ];
    dauChartData: Row[] = [[new Date(), 0, 0]];
    dauChartOptions: Record<string, unknown> = {};
    newUsersList: DashboardNewUserRow[] = [];

    private readonly monthShortDe = [
        'Jan',
        'Feb',
        'Mär',
        'Apr',
        'Mai',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Okt',
        'Nov',
        'Dez',
    ];

    /** Custom vertical cursor (Google crosshair sticks on every category when filters steal hover). */
    animalCursor: { x: number; top: number; height: number } | null = null;
    userCursor: { x: number; top: number; height: number } | null = null;
    private animalChart: google.visualization.CoreChartBase | null = null;
    private userChart: google.visualization.CoreChartBase | null = null;

    loading = false;

    private sub: Subscription;
    private allowed = false;

    private readonly seriesColors: Record<DashboardSeriesId, string> = {
        hit: '#2563eb',
        einstall: '#3d9b4a',
        abgang: '#c2410c',
        tot: '#b91c1c',
        dau: '#7c3aed',
        sessions: '#0891b2',
        hit_melder: '#0f766e',
        new_users: '#db2777',
        new_betriebe: '#ea580c',
    };

    constructor(
        private readonly api: KieneTableService,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly messageService: MessageService,
        private readonly localStorageService: LocalStorageService,
        private readonly router: Router,
        private readonly dialog: MatDialog,
    ) {}

    ngOnInit(): void {
        this.allowed = this.localStorageService.canAccessReporteDashboard();
        if (!this.allowed) {
            this.router.navigate(['/reporte/tierarzt']);
        }
    }

    ngOnDestroy(): void {
        this.sub?.unsubscribe();
    }

    iconFor(id: string): string {
        switch (id) {
            case 'tiere_hit_gemeldet':
            case 'hit':
                return 'cloud_done';
            case 'tiere_lebend':
                return 'pets';
            case 'tiere_einstall':
            case 'einstall':
                return 'login';
            case 'tiere_abgang':
            case 'abgang':
                return 'logout';
            case 'tiere_tot':
            case 'tot':
                return 'dangerous';
            case 'nutzer_dau':
            case 'dau':
                return 'group';
            case 'nutzer_online':
                return 'sensors';
            case 'sessions':
                return 'devices';
            case 'hit_melder':
                return 'how_to_reg';
            case 'new_users':
                return 'person_add';
            case 'new_betriebe':
                return 'add_business';
            default:
                return 'insights';
        }
    }

    onSeriesFilterChange(event: MatSelectChange): void {
        const next = (Array.isArray(event.value) ? event.value : []) as DashboardSeriesId[];
        if (!next.length) {
            // Keep at least one series visible.
            this.seriesFilterCtrl.setValue(this.selectedSeriesIds, { emitEvent: false });
            return;
        }
        this.selectedSeriesIds = next;
        this.animalCursor = null;
        this.rebuildChart();
    }

    onUserSeriesFilterChange(event: MatSelectChange): void {
        const next = (Array.isArray(event.value) ? event.value : []) as DashboardSeriesId[];
        if (!next.length) {
            this.userSeriesFilterCtrl.setValue(this.selectedUserSeriesIds, { emitEvent: false });
            return;
        }
        this.selectedUserSeriesIds = next;
        this.userCursor = null;
        this.rebuildDauChart();
    }

    onAnimalChartReady(event: ChartReadyEvent): void {
        this.animalChart = event.chart as google.visualization.CoreChartBase;
    }

    onUserChartReady(event: ChartReadyEvent): void {
        this.userChart = event.chart as google.visualization.CoreChartBase;
    }

    onAnimalChartMouseMove(event: MouseEvent): void {
        this.animalCursor = this.cursorFromMouse(this.animalChart, this.chartData, event);
    }

    onAnimalChartMouseLeave(event: MouseEvent): void {
        // Ignore leave when moving onto the Google tooltip (blink loop).
        if (this.isChartTooltipTarget(event.relatedTarget)) {
            return;
        }
        this.animalCursor = null;
    }

    onUserChartMouseMove(event: MouseEvent): void {
        this.userCursor = this.cursorFromMouse(this.userChart, this.dauChartData, event);
    }

    onUserChartMouseLeave(event: MouseEvent): void {
        if (this.isChartTooltipTarget(event.relatedTarget)) {
            return;
        }
        this.userCursor = null;
    }

    private isChartTooltipTarget(target: EventTarget | null): boolean {
        return (
            target instanceof Element &&
            !!target.closest('.google-visualization-tooltip')
        );
    }

    openBetriebDialog(): void {
        if (!this.allowed) {
            return;
        }
        const input = new ChangeCurrentClientDialogInput();
        const dialogRef = this.dialog.open(ChangeCurrentClientDialogComponent, {
            minWidth: '1000px',
            maxWidth: '1000px',
            width: '100%',
            data: input,
        });
        dialogRef.afterClosed().subscribe(result => {
            if (!result?.save || !result.client) {
                return;
            }
            this.applyBetriebFilter(result.client as Client);
        });
    }

    clearBetriebFilter(): void {
        if (!this.allowed) {
            return;
        }
        this.selectedBetriebId = null;
        this.selectedBetriebLabel = '';
        this.loadData();
    }

    private applyBetriebFilter(client: Client): void {
        const id = client?.betrieb_id ? Number(client.betrieb_id) : null;
        this.selectedBetriebId = id && id > 0 ? id : null;
        if (this.selectedBetriebId) {
            const parts = [client.kunde_nr_intern, client.bezeichnung_final, client.vvvo].filter(Boolean);
            this.selectedBetriebLabel = parts.join(' · ');
        } else {
            this.selectedBetriebLabel = '';
        }
        this.loadData();
    }

    zeitintervalChanged(event: ZeitauswahlChange): void {
        if (!this.allowed) {
            return;
        }
        this.zeiteintrag = event;
        this.loadData();
    }

    loadData(): void {
        if (!this.zeiteintrag?.von || !this.zeiteintrag?.bis) {
            return;
        }

        let params = new HttpParams();
        params = params.set('von', formatDate(this.zeiteintrag.von, 'yyyy-MM-dd', 'de'));
        params = params.set('bis', formatDate(this.zeiteintrag.bis, 'yyyy-MM-dd', 'de'));
        if (this.selectedBetriebId) {
            params = params.set('betrieb_id', String(this.selectedBetriebId));
        }

        this.loading = true;
        this.animalCursor = null;
        this.userCursor = null;
        this.newUsersList = [];
        this.sub?.unsubscribe();
        this.sub = this.api.get(this.apiBaseUrl + 'report/read_dashboard.php', params).subscribe({
            next: (res: any) => {
                const data = res as DashboardOverview;
                const widgets = Array.isArray(data?.widgets) ? data.widgets : [];
                this.seriesMap = data?.series ?? {};
                this.newUsersList = Array.isArray(data?.new_users_list)
                    ? data.new_users_list.filter(
                          (u): u is DashboardNewUserRow =>
                              !!u && typeof u.erstellt === 'string',
                      )
                    : [];
                this.overview = {
                    von: data?.von ?? '',
                    bis: data?.bis ?? '',
                    betrieb_id: data?.betrieb_id ?? this.selectedBetriebId,
                    compare_von: data?.compare_von,
                    compare_bis: data?.compare_bis,
                    prev_von: data?.prev_von,
                    prev_bis: data?.prev_bis,
                    granularity: data?.granularity,
                    widgets,
                    series: this.seriesMap,
                    new_users_list: this.newUsersList,
                };
                // Series KPIs live under chart filters; keep point-in-time cards above.
                const kpiIds = new Set(['tiere_lebend', 'nutzer_online']);
                this.kpiWidgets = widgets.filter(
                    (w): w is DashboardKpiWidget =>
                        w?.type === 'kpi' &&
                        kpiIds.has(w.id) &&
                        typeof (w as DashboardKpiWidget).value === 'number',
                );

                const animalIds: DashboardSeriesId[] = ['hit', 'einstall', 'abgang', 'tot'];
                this.selectedSeriesIds = this.selectedSeriesIds.filter(
                    id => animalIds.includes(id) && !!this.seriesMap[id],
                );
                if (!this.selectedSeriesIds.length) {
                    this.selectedSeriesIds = ['hit', 'einstall'].filter(id => !!this.seriesMap[id as DashboardSeriesId]) as DashboardSeriesId[];
                    if (!this.selectedSeriesIds.length && this.seriesMap.hit) {
                        this.selectedSeriesIds = ['hit'];
                    }
                }
                this.seriesFilterCtrl.setValue(this.selectedSeriesIds, { emitEvent: false });

                this.selectedUserSeriesIds = this.selectedUserSeriesIds.filter(
                    id => !!this.seriesMap[id],
                );
                if (!this.selectedUserSeriesIds.length) {
                    this.selectedUserSeriesIds = (
                        ['dau', 'sessions', 'hit_melder', 'new_users', 'new_betriebe'] as DashboardSeriesId[]
                    ).filter(id => !!this.seriesMap[id]);
                }
                this.userSeriesFilterCtrl.setValue(this.selectedUserSeriesIds, { emitEvent: false });

                this.rebuildChart();
                this.rebuildDauChart();
                this.loading = false;
            },
            error: err => {
                this.loading = false;
                this.messageService.errorMessage('Dashboard konnte nicht geladen werden.', err);
            },
        });
    }

    private rebuildChart(): void {
        const order: DashboardSeriesId[] = ['hit', 'einstall', 'abgang', 'tot'];
        const selected: DashboardSeries[] = order
            .filter(id => this.selectedSeriesIds.includes(id))
            .map(id => this.seriesMap[id])
            .filter((s): s is DashboardSeries => !!s);

        this.selectedTotals = selected.map(s => this.toSelectedTotal(s));

        if (!selected.length) {
            this.chartColumns = [
                { type: 'date', label: 'Zeitraum' },
                { type: 'number', label: 'Wert' },
            ];
            this.chartData = [[new Date(), 0]];
            this.chartOptions = this.buildChartOptions([]);
            return;
        }

        this.chartColumns = [
            { type: 'date', label: 'Zeitraum' },
            ...selected.map(s => ({ type: 'number', label: s.label } as Column)),
        ];
        const built = this.buildChartRows(selected);
        this.chartData = built.rows;
        this.chartOptions = this.buildChartOptions(
            selected.map(s => s.id as DashboardSeriesId),
            built.axis,
        );
    }

    private rebuildDauChart(): void {
        const order: DashboardSeriesId[] = ['dau', 'sessions', 'hit_melder', 'new_users', 'new_betriebe'];
        this.userSeriesList = order
            .filter(id => this.selectedUserSeriesIds.includes(id))
            .map(id => this.seriesMap[id])
            .filter((s): s is DashboardSeries => !!s);
        this.userTotals = this.userSeriesList.map(s => this.toSelectedTotal(s));

        if (!this.userSeriesList.length) {
            this.dauChartColumns = [
                { type: 'date', label: 'Zeitraum' },
                { type: 'number', label: 'Wert' },
            ];
            this.dauChartData = [[new Date(), 0]];
            this.dauChartOptions = this.buildChartOptions([]);
            return;
        }

        this.dauChartColumns = [
            { type: 'date', label: 'Zeitraum' },
            ...this.userSeriesList.map(s => ({ type: 'number', label: s.label } as Column)),
        ];
        const built = this.buildChartRows(this.userSeriesList);
        this.dauChartData = built.rows;
        this.dauChartOptions = this.buildChartOptions(
            this.userSeriesList.map(s => s.id as DashboardSeriesId),
            built.axis,
        );
    }

    private buildChartRows(seriesList: DashboardSeries[]): {
        rows: Row[];
        axis: { format: string; ticks: Date[]; spanDays: number };
    } {
        const pointCount = seriesList[0]?.points?.length ?? 0;
        const dates: Date[] = [];
        for (let i = 0; i < pointCount; i++) {
            const d = this.parseAxisDate(String(seriesList[0].points[i]?.[0] ?? ''));
            if (d) {
                dates.push(d);
            }
        }
        const axis = this.buildSmartAxis(dates);
        const rows: Row[] = [];
        for (let i = 0; i < pointCount; i++) {
            const d =
                this.parseAxisDate(String(seriesList[0].points[i]?.[0] ?? '')) ||
                new Date();
            const row: Row = [d];
            for (const s of seriesList) {
                row.push(Number(s.points?.[i]?.[1] ?? 0));
            }
            rows.push(row);
        }
        if (!rows.length) {
            rows.push([new Date(), ...seriesList.map(() => 0)]);
        }
        return { rows, axis };
    }

    private toSelectedTotal(s: DashboardSeries): DashboardSelectedTotal {
        const total = Number(s.total) || 0;
        const prevTotal = Number(s.prev_total) || 0;
        const delta = total - prevTotal;
        const deltaPercent = prevTotal === 0 ? null : (delta / prevTotal) * 100;
        const id = s.id as DashboardSeriesId;
        return {
            id: s.id,
            label: s.label,
            total,
            prevTotal,
            delta,
            deltaPercent,
            color: this.seriesColors[id] ?? '#2563eb',
            icon: this.iconFor(id),
        };
    }

    private buildChartOptions(
        ids: DashboardSeriesId[],
        axis?: { format: string; ticks: Date[]; spanDays: number },
    ): Record<string, unknown> {
        const longRange = (axis?.spanDays ?? 0) > 120;
        const hAxis: Record<string, unknown> = {
            textStyle: { fontSize: 11, color: '#6b7280' },
            // Light vertical guides on long ranges help read year/quarter positions.
            gridlines: {
                color: longRange ? '#eef2f7' : 'transparent',
                count: axis?.ticks?.length || 8,
            },
            minorGridlines: { count: 0 },
            baselineColor: '#e5e7eb',
            maxAlternation: 1,
            maxTextLines: 1,
            slantedText: false,
            format: axis?.format || 'dd.MM.yy',
        };
        if (axis?.ticks?.length) {
            hAxis.ticks = axis.ticks.map(d => ({
                v: d,
                f: this.formatTickLabel(d, axis.format),
            }));
        }
        return {
            legend: { position: 'none' },
            chartArea: { left: 56, top: 24, width: '88%', height: '72%' },
            backgroundColor: 'transparent',
            hAxis,
            vAxis: {
                minValue: 0,
                textStyle: { fontSize: 11, color: '#6b7280' },
                baselineColor: '#e5e7eb',
                gridlines: { color: '#f3f4f6' },
            },
            colors: ids.map(id => this.seriesColors[id] ?? '#2563eb'),
            pointSize: longRange ? 3 : 4,
            lineWidth: 2,
            focusTarget: 'category',
            aggregationTarget: 'category',
            tooltip: {
                isHtml: true,
                trigger: 'focus',
                showColorCode: true,
            },
        };
    }

    private parseAxisDate(raw: string): Date | null {
        const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
        if (!m) {
            return null;
        }
        return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    }

    /**
     * Even cadence by span:
     *  days → dd.MM. / dd.MM.yy
     *  ~1 year → monthly (MMM’yy), January = full year
     *  2 years → quarterly (Jan/Apr/Jul/Okt), January = full year
     */
    private buildSmartAxis(dates: Date[]): { format: string; ticks: Date[]; spanDays: number } {
        if (!dates.length) {
            return { format: 'dd.MM.yy', ticks: [], spanDays: 0 };
        }
        const first = this.startOfDay(dates[0]);
        const last = this.startOfDay(dates[dates.length - 1]);
        const spanDays = Math.max(
            1,
            Math.round((last.getTime() - first.getTime()) / 86400000) + 1,
        );

        let format: string;
        let ticks: Date[];
        if (spanDays <= 40) {
            format = 'dd.MM.';
            ticks = this.evenPointTicks(dates, 8);
            this.addYearChangeTicks(dates, ticks);
        } else if (spanDays <= 120) {
            format = 'dd.MM.yy';
            ticks = this.evenPointTicks(dates, 10);
            this.addYearChangeTicks(dates, ticks);
        } else if (spanDays <= 400) {
            format = 'MMM’yy';
            ticks = this.calendarTicks(first, last, 1); // monthly
            if (ticks.length > 14) {
                ticks = this.calendarTicks(first, last, 2); // every 2 months
            }
        } else {
            format = 'MMM’yy';
            ticks = this.quarterTicks(first, last); // even quarters
        }

        this.addYearStartTicks(first, last, ticks);
        ticks.sort((a, b) => a.getTime() - b.getTime());
        return { format, ticks, spanDays };
    }

    /** Evenly spaced ticks taken from actual data points (keeps labels on real values). */
    private evenPointTicks(dates: Date[], target: number): Date[] {
        if (dates.length <= target) {
            return dates.map(d => this.startOfDay(d));
        }
        const ticks: Date[] = [];
        const pushUnique = (d: Date) => {
            const t = this.startOfDay(d).getTime();
            if (!ticks.some(x => x.getTime() === t)) {
                ticks.push(this.startOfDay(d));
            }
        };
        pushUnique(dates[0]);
        const step = (dates.length - 1) / (target - 1);
        for (let i = 1; i < target - 1; i++) {
            pushUnique(dates[Math.round(i * step)]);
        }
        pushUnique(dates[dates.length - 1]);
        return ticks;
    }

    private calendarTicks(first: Date, last: Date, monthStep: number): Date[] {
        const ticks: Date[] = [];
        let cursor = new Date(first.getFullYear(), first.getMonth(), 1);
        if (cursor < first) {
            cursor = new Date(cursor.getFullYear(), cursor.getMonth() + monthStep, 1);
        }
        // Align step to monthStep grid (e.g. odd/even months).
        if (monthStep > 1) {
            const m = cursor.getMonth();
            const aligned = Math.ceil(m / monthStep) * monthStep;
            cursor = new Date(cursor.getFullYear(), aligned, 1);
        }
        while (cursor <= last) {
            ticks.push(new Date(cursor));
            cursor = new Date(cursor.getFullYear(), cursor.getMonth() + monthStep, 1);
        }
        return ticks;
    }

    /** Jan / Apr / Jul / Oct inside the range — even spacing for multi-year views. */
    private quarterTicks(first: Date, last: Date): Date[] {
        const ticks: Date[] = [];
        let year = first.getFullYear();
        let qMonth = Math.floor(first.getMonth() / 3) * 3;
        let cursor = new Date(year, qMonth, 1);
        if (cursor < first) {
            qMonth += 3;
            if (qMonth > 9) {
                qMonth = 0;
                year += 1;
            }
            cursor = new Date(year, qMonth, 1);
        }
        while (cursor <= last) {
            ticks.push(new Date(cursor));
            qMonth += 3;
            if (qMonth > 9) {
                qMonth = 0;
                year = cursor.getFullYear() + 1;
            } else {
                year = cursor.getFullYear();
            }
            cursor = new Date(year, qMonth, 1);
        }
        return ticks;
    }

    private addYearChangeTicks(dates: Date[], ticks: Date[]): void {
        for (let i = 1; i < dates.length; i++) {
            if (dates[i].getFullYear() !== dates[i - 1].getFullYear()) {
                const t = this.startOfDay(dates[i]).getTime();
                if (!ticks.some(x => x.getTime() === t)) {
                    ticks.push(this.startOfDay(dates[i]));
                }
            }
        }
    }

    private addYearStartTicks(first: Date, last: Date, ticks: Date[]): void {
        for (let y = first.getFullYear(); y <= last.getFullYear(); y++) {
            const jan = new Date(y, 0, 1);
            if (jan >= first && jan <= last && !ticks.some(x => x.getTime() === jan.getTime())) {
                ticks.push(jan);
            }
        }
    }

    private formatTickLabel(d: Date, format: string): string {
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yy = String(d.getFullYear()).slice(-2);
        const mon = this.monthShortDe[d.getMonth()];
        if (format === 'dd.MM.') {
            return `${dd}.${mm}.`;
        }
        if (format === 'MMM’yy') {
            // January → full year so year changes read clearly; no space (avoids wrap).
            if (d.getMonth() === 0 && d.getDate() === 1) {
                return String(d.getFullYear());
            }
            return `${mon}’${yy}`;
        }
        return `${dd}.${mm}.${yy}`;
    }

    private startOfDay(d: Date): Date {
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }

    private cursorFromMouse(
        chart: google.visualization.CoreChartBase | null,
        data: Row[],
        event: MouseEvent,
    ): { x: number; top: number; height: number } | null {
        const rowCount = data?.length ?? 0;
        if (!chart || rowCount < 1) {
            return null;
        }
        try {
            const chartAny = chart as any;
            const cli = chartAny.getChartLayoutInterface?.();
            const container: HTMLElement | null =
                (typeof chartAny.getContainer === 'function' ? chartAny.getContainer() : null) ??
                chartAny.container ??
                null;
            if (!cli || !container) {
                return null;
            }
            const area = cli.getChartAreaBoundingBox();
            if (!area) {
                return null;
            }

            // Layout coords are relative to the chart container, not the wrap.
            const wrap = event.currentTarget as HTMLElement;
            const wrapRect = wrap.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const originX = containerRect.left - wrapRect.left;
            const originY = containerRect.top - wrapRect.top;
            const mouseX = event.clientX - containerRect.left;
            if (mouseX < area.left || mouseX > area.left + area.width) {
                return null;
            }

            let bestX = area.left;
            let bestDist = Number.POSITIVE_INFINITY;
            for (let i = 0; i < rowCount; i++) {
                const cx = this.xLocationForRow(cli, data, i);
                if (cx == null) {
                    continue;
                }
                const dist = Math.abs(cx - mouseX);
                if (dist < bestDist) {
                    bestDist = dist;
                    bestX = cx;
                }
            }
            if (!Number.isFinite(bestDist) || bestDist === Number.POSITIVE_INFINITY) {
                return null;
            }
            return {
                x: bestX + originX,
                top: area.top + originY,
                height: area.height,
            };
        } catch {
            return null;
        }
    }

    /** Pixel X of a series point — domain value first, then category box. */
    private xLocationForRow(cli: any, data: Row[], row: number): number | null {
        const domain = data[row]?.[0];
        if (domain != null && typeof cli.getXLocation === 'function') {
            try {
                const byValue = Number(cli.getXLocation(domain));
                if (Number.isFinite(byValue)) {
                    return byValue;
                }
            } catch {
                // fall through
            }
            try {
                const byIndex = Number(cli.getXLocation(row));
                if (Number.isFinite(byIndex)) {
                    return byIndex;
                }
            } catch {
                // fall through
            }
        }
        try {
            const box = cli.getBoundingBox?.('category#' + row);
            if (box && Number.isFinite(box.left) && Number.isFinite(box.width)) {
                return box.left + box.width / 2;
            }
        } catch {
            // fall through
        }
        return null;
    }
}
